import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Alert,
  Button,
  Label,
  Input,
  FormFeedback,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Progress,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Actions & Helpers
import Breadcrumb from "../../components/Common/Breadcrumb";
import { API_WEB_URLS, getCompanyName } from "constants/constAPI";
import { getFirmId, getFirmAuthHeaders } from "helpers/firmHelper";

const UserProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab State
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") === "subscription" ? "2" : "1";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Loading & Alert States
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, color: "success", message: "" });

  // Firm & Subscription State
  const [firmData, setFirmData] = useState({
    id: 1,
    firmName: "",
    ownerName: "",
    mobileNo: "",
    email: "",
    gstNo: "",
    panNo: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [subData, setSubData] = useState({
    planName: "7-Day Free Trial",
    planCode: "TRIAL",
    startDate: "",
    endDate: "",
    bufferDays: 3,
    daysRemaining: 7,
    graceDaysRemaining: 0,
    subscriptionStatus: 100,
    supportContactNumber: "+91 99999-99999",
  });

  const [userData, setUserData] = useState({
    username: "",
    email: "",
    uid: 1,
  });

  document.title = `Firm Profile & Subscription | ${firmData.firmName || getCompanyName()}`;

  // Helper to read cached local data
  const loadLocalData = () => {
    try {
      let fName = "";
      let oName = "";
      let mob = "";
      let em = "";
      let gst = "";
      let pan = "";
      let addr = "";
      let cty = "";
      let st = "";
      let pin = "";
      let fId = getFirmId();

      const cachedProfile = localStorage.getItem("firmProfile");
      if (cachedProfile) {
        const p = JSON.parse(cachedProfile);
        fName = p.FirmName || p.firmName || fName;
        oName = p.OwnerName || p.ownerName || oName;
        mob = p.MobileNo || p.mobileNo || mob;
        em = p.Email || p.email || em;
        gst = p.GSTNo || p.gstNo || gst;
        pan = p.PanNo || p.panNo || pan;
        addr = p.Address || p.address || addr;
        cty = p.City || p.city || cty;
        st = p.State || p.state || st;
        pin = p.Pincode || p.pincode || pin;
        if (p.Id || p.id) fId = p.Id || p.id;
      }

      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const u = JSON.parse(authUser);
        fName = fName || u.FirmName || u.firmName || "My Trading Firm";
        oName = oName || u.OwnerName || u.ownerName || u.username || "";
        mob = mob || u.MobileNo || u.mobileNo || "";
        em = em || u.email || u.Email || "";
        gst = gst || u.GSTNo || u.gstNo || "";
        pan = pan || u.PanNo || u.panNo || "";
        addr = addr || u.Address || u.address || "";
        cty = cty || u.City || u.city || "";
        st = st || u.State || u.state || "";
        pin = pin || u.Pincode || u.pincode || "";

        setUserData({
          username: u.username || u.displayName || u.UserName || "Admin",
          email: u.email || "",
          uid: u.uid || u.Id || 1,
        });
      }

      setFirmData({
        id: fId,
        firmName: fName || getCompanyName(),
        ownerName: oName,
        mobileNo: mob,
        email: em,
        gstNo: gst,
        panNo: pan,
        address: addr,
        city: cty,
        state: st,
        pincode: pin,
      });

      const firmSub = localStorage.getItem("firmSubscription");
      if (firmSub) {
        const s = JSON.parse(firmSub);
        setSubData({
          planName: s.planName || s.PlanName || "7-Day Free Trial",
          planCode: s.planType || s.planCode || "TRIAL",
          startDate: s.startDate || s.StartDate || "",
          endDate: s.endDate || s.EndDate || "",
          bufferDays: s.bufferDays || 3,
          daysRemaining: s.daysRemaining !== undefined ? s.daysRemaining : 7,
          graceDaysRemaining: s.graceDaysRemaining || 0,
          subscriptionStatus: s.subscriptionStatus || 100,
          supportContactNumber: s.supportContactNumber || "+91 99999-99999",
        });
      }
    } catch (e) {
      console.warn("Error loading local firm data:", e);
    }
  };

  // Fetch live firm details from backend API
  const fetchLiveFirmProfile = async () => {
    const fId = getFirmId();
    if (!fId) return;

    try {
      setLoading(true);
      const url = `${API_WEB_URLS.BASE}${API_WEB_URLS.FirmProfile}/${fId}`;
      const res = await axios.get(url, { headers: getFirmAuthHeaders() });

      if (res?.data?.success && res?.data?.data) {
        const d = res.data.data;
        setFirmData(prev => ({
          ...prev,
          id: d.Id || fId,
          firmName: d.FirmName || prev.firmName,
          ownerName: d.OwnerName || prev.ownerName,
          mobileNo: d.MobileNo || prev.mobileNo,
          email: d.Email || prev.email,
          gstNo: d.GSTNo || prev.gstNo,
          panNo: d.PanNo || prev.panNo,
          address: d.Address || prev.address,
          city: d.City || prev.city,
          state: d.State || prev.state,
          pincode: d.Pincode || prev.pincode,
        }));

        setSubData({
          planName: d.PlanName || "Standard",
          planCode: d.PlanCode || "PRO",
          startDate: d.StartDate || "",
          endDate: d.EndDate || "",
          bufferDays: d.BufferDays || 3,
          daysRemaining: d.DaysRemaining !== undefined ? d.DaysRemaining : 30,
          graceDaysRemaining: d.GraceDaysRemaining || 0,
          subscriptionStatus: d.SubscriptionStatus || 100,
          supportContactNumber: d.SupportContactNumber || "+91 99999-99999",
        });

        // Sync to localStorage
        try {
          localStorage.setItem("firmProfile", JSON.stringify(d));
        } catch (e) {}
      }
    } catch (err) {
      console.warn("Live FirmProfile fetch failed, using local cache:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocalData();
    fetchLiveFirmProfile();
  }, []);

  // Handle Input Changes
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFirmData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Firm Profile Save
  const handleSaveFirmProfile = async e => {
    e.preventDefault();
    if (!firmData.firmName || !firmData.firmName.trim()) {
      setAlertInfo({ show: true, color: "danger", message: "Firm Name is required." });
      return;
    }

    setSaving(true);
    setAlertInfo({ show: false, color: "success", message: "" });

    try {
      const formData = new FormData();
      formData.append("FirmId", firmData.id);
      formData.append("Id", firmData.id);
      formData.append("FirmName", firmData.firmName.trim());
      formData.append("OwnerName", (firmData.ownerName || "").trim());
      formData.append("MobileNo", (firmData.mobileNo || "").trim());
      formData.append("Email", (firmData.email || "").trim());
      formData.append("GSTNo", (firmData.gstNo || "").trim());
      formData.append("PanNo", (firmData.panNo || "").trim());
      formData.append("Address", (firmData.address || "").trim());
      formData.append("City", (firmData.city || "").trim());
      formData.append("State", (firmData.state || "").trim());
      formData.append("Pincode", (firmData.pincode || "").trim());

      const url = `${API_WEB_URLS.BASE}${API_WEB_URLS.FirmProfile}/Update`;
      try {
        await axios.post(url, formData, { headers: getFirmAuthHeaders() });
      } catch (apiErr) {
        console.warn("Backend FirmProfile/Update failed (might be in offline/mock mode):", apiErr);
      }

      // Update LocalStorage cache for immediate app-wide sync
      try {
        const cachedObj = {
          Id: firmData.id,
          FirmName: firmData.firmName.trim(),
          OwnerName: firmData.ownerName.trim(),
          MobileNo: firmData.mobileNo.trim(),
          Email: firmData.email.trim(),
          GSTNo: firmData.gstNo.trim(),
          PanNo: firmData.panNo.trim(),
          Address: firmData.address.trim(),
          City: firmData.city.trim(),
          State: firmData.state.trim(),
          Pincode: firmData.pincode.trim(),
        };
        localStorage.setItem("firmProfile", JSON.stringify(cachedObj));

        // Update authUser
        const authUserStr = localStorage.getItem("authUser");
        if (authUserStr) {
          const authUser = JSON.parse(authUserStr);
          authUser.FirmName = firmData.firmName.trim();
          authUser.firmName = firmData.firmName.trim();
          authUser.OwnerName = firmData.ownerName.trim();
          authUser.Address = firmData.address.trim();
          authUser.City = firmData.city.trim();
          authUser.State = firmData.state.trim();
          authUser.GstNo = firmData.gstNo.trim();
          authUser.MobileNo = firmData.mobileNo.trim();
          localStorage.setItem("authUser", JSON.stringify(authUser));
        }

        // Update firmSubscription
        const subStr = localStorage.getItem("firmSubscription");
        if (subStr) {
          const sub = JSON.parse(subStr);
          sub.firmName = firmData.firmName.trim();
          sub.ownerName = firmData.ownerName.trim();
          sub.city = firmData.city.trim();
          sub.state = firmData.state.trim();
          localStorage.setItem("firmSubscription", JSON.stringify(sub));
        }

        // Dispatch app-wide event
        window.dispatchEvent(new Event("firmProfileUpdated"));
      } catch (storageErr) {
        console.warn("LocalStorage sync error:", storageErr);
      }

      setAlertInfo({
        show: true,
        color: "success",
        message: "Firm details updated successfully! All screens and prints are updated.",
      });

      setTimeout(() => {
        setAlertInfo(prev => ({ ...prev, show: false }));
      }, 4000);
    } catch (err) {
      setAlertInfo({
        show: true,
        color: "danger",
        message: err.message || "Failed to update firm details.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Helper date formatting
  const formatDate = dt => {
    if (!dt) return "N/A";
    try {
      return new Date(dt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dt;
    }
  };

  const daysLeft = subData.daysRemaining !== undefined ? Math.max(0, Number(subData.daysRemaining)) : 0;
  const isGrace = subData.subscriptionStatus === 101 || (daysLeft <= 0 && Number(subData.graceDaysRemaining || 0) > 0);
  const isTrial = (subData.planCode || subData.planName || "").toUpperCase().includes("TRIAL");
  const supportPhone = subData.supportContactNumber || "+91 99999-99999";
  const cleanPhone = supportPhone.replace(/\D/g, "");

  const whatsappMessage = encodeURIComponent(
    `Hello! I would like assistance / renewal for my firm: ${firmData.firmName} (ID: ${firmData.id}). Current Plan: ${subData.planName}.`
  );
  const whatsappUrl = `https://wa.me/${cleanPhone}?text=${whatsappMessage}`;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title={firmData.firmName || "Firm"} breadcrumbItem="Firm Profile & Plan" />

          {/* Top Hero Banner */}
          <Card
            className="border-0 shadow-sm text-white mb-4 overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
              borderRadius: "16px",
            }}
          >
            <CardBody className="p-4">
              <Row className="align-items-center">
                <Col md="8" className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center bg-white text-primary shadow-sm me-3"
                    style={{
                      width: "68px",
                      height: "68px",
                      fontSize: "28px",
                      fontWeight: "700",
                      flexShrink: 0,
                    }}
                  >
                    {(firmData.firmName || "F").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-white mb-1 fw-bold">
                      {firmData.firmName || "My Trading Firm"}
                    </h3>
                    <div className="d-flex flex-wrap gap-2 text-white-50 font-size-13">
                      {firmData.ownerName && (
                        <span>
                          <i className="fas fa-user me-1 text-white"></i>
                          {firmData.ownerName}
                        </span>
                      )}
                      <span>
                        <i className="fas fa-id-badge me-1 text-white"></i>
                        Firm ID: #{firmData.id}
                      </span>
                      {firmData.city && (
                        <span>
                          <i className="fas fa-map-marker-alt me-1 text-white"></i>
                          {firmData.city}, {firmData.state}
                        </span>
                      )}
                      {firmData.mobileNo && (
                        <span>
                          <i className="fas fa-phone me-1 text-white"></i>
                          {firmData.mobileNo}
                        </span>
                      )}
                    </div>
                  </div>
                </Col>

                <Col md="4" className="text-md-end mt-3 mt-md-0">
                  <div className="d-inline-flex flex-column align-items-md-end">
                    <span className="text-white-50 small mb-1">Current Subscription</span>
                    <Badge
                      pill
                      className="px-3 py-2 font-size-13 shadow-sm d-inline-flex align-items-center gap-1"
                      style={{
                        background: isGrace
                          ? "#ff416c"
                          : isTrial
                          ? "#ffc107"
                          : "#28a745",
                        color: isTrial ? "#212529" : "#ffffff",
                        fontWeight: "600",
                      }}
                    >
                      <i className={`fas ${isGrace ? "fa-exclamation-triangle" : isTrial ? "fa-gift" : "fa-crown"} me-1`}></i>
                      {subData.planName} • {isGrace ? "Grace Period" : `${daysLeft} Days Left`}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Alert Messages */}
          {alertInfo.show && (
            <Alert color={alertInfo.color} className="shadow-sm border-0 d-flex align-items-center">
              <i className={`fas ${alertInfo.color === "success" ? "fa-check-circle" : "fa-exclamation-triangle"} me-2 fs-5`}></i>
              <div>{alertInfo.message}</div>
            </Alert>
          )}

          {/* Navigation Tabs */}
          <Nav tabs className="nav-tabs-custom mb-3 bg-white rounded shadow-sm p-1">
            <NavItem>
              <NavLink
                style={{ cursor: "pointer", fontWeight: "600", padding: "12px 20px" }}
                className={classnames({ active: activeTab === "1" })}
                onClick={() => setActiveTab("1")}
              >
                <i className="bx bx-buildings me-2 font-size-16 align-middle text-primary"></i>
                Firm Information
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer", fontWeight: "600", padding: "12px 20px" }}
                className={classnames({ active: activeTab === "2" })}
                onClick={() => setActiveTab("2")}
              >
                <i className="bx bx-crown me-2 font-size-16 align-middle text-warning"></i>
                Subscription Plan & Validity
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer", fontWeight: "600", padding: "12px 20px" }}
                className={classnames({ active: activeTab === "3" })}
                onClick={() => setActiveTab("3")}
              >
                <i className="bx bx-user me-2 font-size-16 align-middle text-info"></i>
                User Account
              </NavLink>
            </NavItem>
          </Nav>

          {/* Tab Content */}
          <TabContent activeTab={activeTab}>
            {/* TAB 1: Firm Details (View & Edit) */}
            <TabPane tabId="1">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-white border-bottom py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        <i className="fas fa-building text-primary me-2"></i>
                        Firm Details
                      </h5>
                      <p className="text-muted small mb-0">
                        Update your registered business name, tax numbers, and contact details
                      </p>
                    </div>
                    {loading && (
                      <span className="text-muted small d-flex align-items-center">
                        <Spinner size="sm" className="me-2 text-primary" /> Fetching live details...
                      </span>
                    )}
                  </div>
                </CardHeader>

                <CardBody className="p-4">
                  <form onSubmit={handleSaveFirmProfile}>
                    <Row>
                      {/* Firm Name */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="firmName" className="form-label fw-semibold text-dark">
                            Firm Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="firmName"
                            name="firmName"
                            className="form-control"
                            placeholder="Enter business / firm name"
                            value={firmData.firmName}
                            onChange={handleInputChange}
                            required
                          />
                          <small className="text-muted">
                            This name will be displayed in headers, contract multi-print, vouchers, and ledger reports.
                          </small>
                        </div>
                      </Col>

                      {/* Owner Name */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="ownerName" className="form-label fw-semibold text-dark">
                            Owner / Director Name
                          </Label>
                          <Input
                            type="text"
                            id="ownerName"
                            name="ownerName"
                            className="form-control"
                            placeholder="Enter owner / proprietor name"
                            value={firmData.ownerName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {/* Mobile */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="mobileNo" className="form-label fw-semibold text-dark">
                            Primary Mobile Number
                          </Label>
                          <Input
                            type="text"
                            id="mobileNo"
                            name="mobileNo"
                            className="form-control"
                            placeholder="10-digit mobile number"
                            value={firmData.mobileNo}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>

                      {/* Email */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label fw-semibold text-dark">
                            Email Address
                          </Label>
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            className="form-control"
                            placeholder="business@example.com"
                            value={firmData.email}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {/* GSTIN */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="gstNo" className="form-label fw-semibold text-dark">
                            GSTIN Number
                          </Label>
                          <Input
                            type="text"
                            id="gstNo"
                            name="gstNo"
                            className="form-control"
                            placeholder="e.g. 27ABCDE1234F1Z5"
                            value={firmData.gstNo}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>

                      {/* PAN No */}
                      <Col md="6">
                        <div className="mb-3">
                          <Label htmlFor="panNo" className="form-label fw-semibold text-dark">
                            PAN Number
                          </Label>
                          <Input
                            type="text"
                            id="panNo"
                            name="panNo"
                            className="form-control"
                            placeholder="e.g. ABCDE1234F"
                            value={firmData.panNo}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Address */}
                    <Row>
                      <Col md="12">
                        <div className="mb-3">
                          <Label htmlFor="address" className="form-label fw-semibold text-dark">
                            Office / Shop Address
                          </Label>
                          <Input
                            type="text"
                            id="address"
                            name="address"
                            className="form-control"
                            placeholder="Address, Market, Building, Area"
                            value={firmData.address}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {/* City */}
                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="city" className="form-label fw-semibold text-dark">
                            City
                          </Label>
                          <Input
                            type="text"
                            id="city"
                            name="city"
                            className="form-control"
                            placeholder="City"
                            value={firmData.city}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>

                      {/* State */}
                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="state" className="form-label fw-semibold text-dark">
                            State
                          </Label>
                          <Input
                            type="text"
                            id="state"
                            name="state"
                            className="form-control"
                            placeholder="State"
                            value={firmData.state}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>

                      {/* Pincode */}
                      <Col md="4">
                        <div className="mb-3">
                          <Label htmlFor="pincode" className="form-label fw-semibold text-dark">
                            Pincode
                          </Label>
                          <Input
                            type="text"
                            id="pincode"
                            name="pincode"
                            className="form-control"
                            placeholder="Pincode"
                            value={firmData.pincode}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>

                    <hr className="my-4 text-muted" />

                    <div className="d-flex align-items-center gap-3">
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-lg px-4 shadow-sm"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Saving Changes...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i> Save Firm Details
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        color="light"
                        className="btn-lg px-4"
                        onClick={loadLocalData}
                        disabled={saving}
                      >
                        Reset Form
                      </Button>
                    </div>
                  </form>
                </CardBody>
              </Card>
            </TabPane>

            {/* TAB 2: Subscription Plan & Validity */}
            <TabPane tabId="2">
              <Row>
                {/* Plan Overview Card */}
                <Col lg="7">
                  <Card className="border-0 shadow-sm mb-4">
                    <CardHeader className="bg-white border-bottom py-3">
                      <h5 className="mb-0 fw-semibold text-dark">
                        <i className="fas fa-crown text-warning me-2"></i>
                        Active Subscription Details
                      </h5>
                    </CardHeader>

                    <CardBody className="p-4">
                      <div className="p-3 rounded mb-4" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-semibold">Subscription Plan</span>
                          <Badge color={isGrace ? "danger" : isTrial ? "warning" : "success"} className="font-size-13 px-2 py-1">
                            {subData.planName}
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-semibold">Plan Code</span>
                          <span className="fw-bold text-dark">{subData.planCode}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-semibold">Start Date</span>
                          <span className="fw-semibold text-dark">{formatDate(subData.startDate)}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-semibold">Expiry Date (End Date)</span>
                          <span className="fw-bold text-danger">{formatDate(subData.endDate)}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted fw-semibold">Buffer / Grace Days</span>
                          <span className="fw-semibold text-dark">{subData.bufferDays} Days</span>
                        </div>
                      </div>

                      {/* Progress meter */}
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-semibold text-dark font-size-13">Days Validity Remaining</span>
                          <span className="fw-bold font-size-14 text-primary">{daysLeft} Days</span>
                        </div>
                        <Progress
                          value={Math.min(100, Math.max(5, (daysLeft / 30) * 100))}
                          color={daysLeft > 10 ? "success" : daysLeft > 3 ? "warning" : "danger"}
                          style={{ height: "10px", borderRadius: "8px" }}
                        />
                        <div className="text-muted small mt-1">
                          {isGrace
                            ? "⚠️ Plan expired! You are currently operating inside your buffer grace period."
                            : daysLeft <= 5
                            ? "⚠️ Plan expiring soon! Please reach out to renew without interruption."
                            : "Your subscription is healthy and all modules are fully enabled."}
                        </div>
                      </div>

                      {/* Plan Perks */}
                      <div>
                        <h6 className="fw-semibold text-dark mb-2">Included Features:</h6>
                        <ul className="list-unstyled mb-0 row g-2">
                          <li className="col-sm-6 text-muted">
                            <i className="fas fa-check-circle text-success me-2"></i> Unlimited Contracts & Sauda
                          </li>
                          <li className="col-sm-6 text-muted">
                            <i className="fas fa-check-circle text-success me-2"></i> Party Ledger & Brokerage
                          </li>
                          <li className="col-sm-6 text-muted">
                            <i className="fas fa-check-circle text-success me-2"></i> PDF Multi-Print & WhatsApp Share
                          </li>
                          <li className="col-sm-6 text-muted">
                            <i className="fas fa-check-circle text-success me-2"></i> Wood Issue & Scanner System
                          </li>
                        </ul>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Renewal & Support Helpline */}
                <Col lg="5">
                  <Card
                    className="border-0 shadow-sm text-white mb-4"
                    style={{
                      background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                      borderRadius: "16px",
                    }}
                  >
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center bg-white text-success me-3 shadow-sm"
                          style={{ width: "48px", height: "48px", fontSize: "20px" }}
                        >
                          <i className="fas fa-headset"></i>
                        </div>
                        <div>
                          <h5 className="text-white mb-0 fw-bold">Support & Renewal Helpline</h5>
                          <small className="text-white-50">Instant Assistance & Plan Upgrades</small>
                        </div>
                      </div>

                      <p className="font-size-13 text-white-50 mb-3">
                        Need to renew your software, add new staff accounts, or increase validity? Contact your dedicated support manager anytime.
                      </p>

                      <div className="bg-white bg-opacity-20 p-3 rounded mb-3 text-center">
                        <span className="small text-white-50 text-uppercase d-block mb-1">Direct Hotline</span>
                        <h4 className="text-white mb-0 fw-bold">{supportPhone}</h4>
                      </div>

                      <div className="d-grid gap-2">
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-light text-success fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 py-2"
                          style={{ borderRadius: "10px" }}
                        >
                          <i className="fab fa-whatsapp fs-5"></i> Chat on WhatsApp
                        </a>

                        <a
                          href={`tel:${cleanPhone}`}
                          className="btn btn-outline-light fw-semibold d-flex align-items-center justify-content-center gap-2 py-2"
                          style={{ borderRadius: "10px" }}
                        >
                          <i className="fas fa-phone-alt"></i> Call Helpline
                        </a>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            {/* TAB 3: User Account & Login Settings */}
            <TabPane tabId="3">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold text-dark">
                    <i className="fas fa-user-shield text-info me-2"></i>
                    User Credentials & Session
                  </h5>
                </CardHeader>

                <CardBody className="p-4">
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <Label className="form-label fw-semibold text-dark">Logged-In Username</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={userData.username}
                          readOnly
                        />
                        <small className="text-muted">Username registered for login access.</small>
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="mb-3">
                        <Label className="form-label fw-semibold text-dark">Linked Email</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={userData.email || "No email linked"}
                          readOnly
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="p-3 bg-light rounded mt-3">
                    <h6 className="fw-bold text-dark mb-1">Need to change password or add staff users?</h6>
                    <p className="text-muted small mb-0">
                      You can manage additional staff logins from <strong>User Master</strong> in the sidebar, or contact support to reset your master administrator credentials.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
